import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableHeader, 
    TableRow 
} from '@/components/ui/table';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Eye, Monitor, Smartphone, Globe, RefreshCw, ChevronLeft, ChevronRight, BarChart3, Search, X, LogIn, User, Clock } from 'lucide-react';
import { adminAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    Legend
} from 'recharts';

const AdminVisitorsTab = () => {
    const [visitors, setVisitors] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingStats, setLoadingStats] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [period, setPeriod] = useState('1'); // Default to 1 day
    const [uniquePeriod, setUniquePeriod] = useState('1'); // Separate period for unique visitors chart
    const [uniqueStats, setUniqueStats] = useState(null); // Separate stats for unique visitors chart
    const [loadingUniqueStats, setLoadingUniqueStats] = useState(true);
    const [ipFilter, setIpFilter] = useState('');
    const [ipSearch, setIpSearch] = useState('');
    const [urlFilter, setUrlFilter] = useState('');
    const [uniqueIps, setUniqueIps] = useState([]);
    const [todayLogins, setTodayLogins] = useState([]);
    const [loadingLogins, setLoadingLogins] = useState(true);
    const [activeVisitors, setActiveVisitors] = useState({ visitors: [], total: 0, logged_in: 0, anonymous: 0 });
    const [loadingActive, setLoadingActive] = useState(true);
    const [uniqueTodayModal, setUniqueTodayModal] = useState(false);
    const [uniqueTodayVisitors, setUniqueTodayVisitors] = useState([]);
    const [loadingUniqueTodayVisitors, setLoadingUniqueTodayVisitors] = useState(false);
    const [selectedVisitorPages, setSelectedVisitorPages] = useState(null); // For showing pages popup
    const [selectedVisitorActions, setSelectedVisitorActions] = useState(null); // For showing actions popup
    const [expandedPagesIp, setExpandedPagesIp] = useState(null); // For expanding all pages
    const limit = 50;

    const fetchVisitors = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getVisitors(page * limit, limit, ipFilter || null, urlFilter || null);
            setVisitors(res.data.visitors || []);
            setTotal(res.data.total || 0);
            if (res.data.unique_ips) {
                setUniqueIps(res.data.unique_ips);
            }
        } catch (error) {
            toast.error('Eroare la încărcarea vizitatorilor');
        } finally {
            setLoading(false);
        }
    }, [page, ipFilter, urlFilter]);

    const fetchStats = useCallback(async () => {
        setLoadingStats(true);
        try {
            const res = await adminAPI.getVisitorsStats(parseInt(period));
            setStats(res.data);
        } catch (error) {
            toast.error('Eroare la încărcarea statisticilor');
        } finally {
            setLoadingStats(false);
        }
    }, [period]);

    const fetchUniqueStats = useCallback(async () => {
        setLoadingUniqueStats(true);
        try {
            const res = await adminAPI.getVisitorsStats(parseInt(uniquePeriod));
            setUniqueStats(res.data);
        } catch (error) {
            toast.error('Eroare la încărcarea statisticilor vizitatori unici');
        } finally {
            setLoadingUniqueStats(false);
        }
    }, [uniquePeriod]);

    useEffect(() => {
        fetchVisitors();
    }, [fetchVisitors]);

    useEffect(() => {
        fetchStats();
        // Auto-refresh stats every 30 seconds
        const interval = setInterval(fetchStats, 30000);
        return () => clearInterval(interval);
    }, [fetchStats]);

    useEffect(() => {
        fetchUniqueStats();
        // Auto-refresh unique stats every 30 seconds
        const interval = setInterval(fetchUniqueStats, 30000);
        return () => clearInterval(interval);
    }, [fetchUniqueStats]);

    // Fetch today's logins
    const fetchTodayLogins = useCallback(async () => {
        setLoadingLogins(true);
        try {
            const res = await api.get('/admin/visitors/logins-today');
            setTodayLogins(res.data.logins || []);
        } catch (error) {
            console.error('Error fetching today logins:', error);
        } finally {
            setLoadingLogins(false);
        }
    }, []);

    useEffect(() => {
        fetchTodayLogins();
        // Auto-refresh logins every 30 seconds
        const interval = setInterval(fetchTodayLogins, 30000);
        return () => clearInterval(interval);
    }, [fetchTodayLogins]);

    // Fetch active visitors (real-time)
    const fetchActiveVisitors = useCallback(async () => {
        setLoadingActive(true);
        try {
            const res = await api.get('/admin/visitors/active');
            setActiveVisitors(res.data);
        } catch (error) {
            console.error('Error fetching active visitors:', error);
        } finally {
            setLoadingActive(false);
        }
    }, []);

    // Fetch unique visitors today for modal
    const fetchUniqueTodayVisitors = useCallback(async () => {
        setLoadingUniqueTodayVisitors(true);
        try {
            const response = await api.get('/admin/visitors/unique-today');
            setUniqueTodayVisitors(response.data.visitors || []);
        } catch (error) {
            console.error('Error fetching unique visitors today:', error);
            toast.error('Eroare la încărcarea vizitatorilor unici');
        } finally {
            setLoadingUniqueTodayVisitors(false);
        }
    }, []);

    // Open modal and fetch data
    const handleOpenUniqueTodayModal = () => {
        setUniqueTodayModal(true);
        fetchUniqueTodayVisitors();
    };

    useEffect(() => {
        fetchActiveVisitors();
        // Refresh every 30 seconds
        const interval = setInterval(fetchActiveVisitors, 30000);
        return () => clearInterval(interval);
    }, [fetchActiveVisitors]);

    const parseUserAgent = (ua) => {
        if (!ua || ua === 'unknown') return { device: 'Unknown', browser: 'Unknown', os: 'Unknown' };
        
        let device = 'Desktop';
        let browser = 'Unknown';
        let os = 'Unknown';
        
        // Detect device
        if (/mobile/i.test(ua)) device = 'Mobile';
        else if (/tablet|ipad/i.test(ua)) device = 'Tablet';
        
        // Detect browser
        if (/chrome/i.test(ua) && !/edge|edg/i.test(ua)) browser = 'Chrome';
        else if (/firefox/i.test(ua)) browser = 'Firefox';
        else if (/safari/i.test(ua) && !/chrome/i.test(ua)) browser = 'Safari';
        else if (/edge|edg/i.test(ua)) browser = 'Edge';
        else if (/opera|opr/i.test(ua)) browser = 'Opera';
        
        // Detect OS
        if (/windows/i.test(ua)) os = 'Windows';
        else if (/macintosh|mac os/i.test(ua)) os = 'macOS';
        else if (/linux/i.test(ua) && !/android/i.test(ua)) os = 'Linux';
        else if (/android/i.test(ua)) os = 'Android';
        else if (/iphone|ipad|ipod/i.test(ua)) os = 'iOS';
        
        return { device, browser, os };
    };

    const formatDate = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleString('ro-RO', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    // Calculate time spent on site (difference between last_visit and first_visit)
    const formatTimeSpent = (firstVisit, lastVisit) => {
        if (!firstVisit || !lastVisit) return '-';
        
        try {
            const start = new Date(firstVisit);
            const end = new Date(lastVisit);
            const diffMs = end - start;
            
            if (diffMs < 0) return '-';
            if (diffMs < 1000) return '<1s';
            
            const diffSeconds = Math.floor(diffMs / 1000);
            const diffMinutes = Math.floor(diffSeconds / 60);
            const diffHours = Math.floor(diffMinutes / 60);
            
            if (diffHours > 0) {
                const remainingMinutes = diffMinutes % 60;
                return `${diffHours}h ${remainingMinutes}m`;
            } else if (diffMinutes > 0) {
                const remainingSeconds = diffSeconds % 60;
                return `${diffMinutes}m ${remainingSeconds}s`;
            } else {
                return `${diffSeconds}s`;
            }
        } catch {
            return '-';
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-4 w-full overflow-x-hidden" style={{ maxWidth: '100vw' }}>
            {/* Today's Unique Visitors - Highlighted Card - CLICKABLE */}
            <Card 
                className="overflow-hidden bg-emerald-50 border-emerald-200 cursor-pointer hover:bg-emerald-100 transition-colors"
                onClick={handleOpenUniqueTodayModal}
            >
                <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Globe className="w-8 h-8 text-emerald-500" />
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-emerald-600 font-medium">Vizitatori unici azi</p>
                                    <div className="flex items-center gap-1 text-[10px] text-emerald-500">
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                        <span>LIVE</span>
                                    </div>
                                    <span className="text-[10px] text-emerald-400">(click pentru detalii)</span>
                                </div>
                                <p className="text-2xl font-bold text-emerald-700">
                                    {loadingStats ? <Skeleton className="h-8 w-20" /> : stats?.summary?.today_unique_visitors || 0}
                                </p>
                            </div>
                        </div>
                        <div className="text-right text-sm text-emerald-600">
                            <span className="block">din {stats?.summary?.today_visits || 0} vizite</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Active Visitors RIGHT NOW */}
            <Card className="overflow-hidden border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50">
                <CardHeader className="p-3 sm:p-4 pb-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                                <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75" />
                            </div>
                            <CardTitle className="text-base sm:text-lg text-green-700">
                                Vizitatori activi acum
                            </CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant="default" className="bg-green-600 text-lg px-3">
                                {activeVisitors.total}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={fetchActiveVisitors} disabled={loadingActive}>
                                <RefreshCw className={`w-4 h-4 ${loadingActive ? 'animate-spin' : ''}`} />
                            </Button>
                        </div>
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {activeVisitors.logged_in} autentificați
                        </span>
                        <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {activeVisitors.anonymous} anonimi
                        </span>
                    </div>
                </CardHeader>
                <CardContent className="p-2 sm:p-4 pt-0">
                    {loadingActive ? (
                        <div className="space-y-2">
                            {[1, 2].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                        </div>
                    ) : activeVisitors.visitors.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground">
                            <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Niciun vizitator activ în acest moment</p>
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {activeVisitors.visitors.map((visitor, idx) => {
                                const ua = parseUserAgent(visitor.user_agent);
                                return (
                                    <div 
                                        key={visitor.visitor_key || idx}
                                        className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg bg-white/70 hover:bg-white transition-colors border border-green-100"
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            {visitor.is_logged_in && visitor.user ? (
                                                <>
                                                    <Avatar className="h-8 w-8 shrink-0 ring-2 ring-green-400">
                                                        <AvatarImage src={visitor.user.profile_image} />
                                                        <AvatarFallback className="text-xs bg-green-100 text-green-700">
                                                            {(visitor.user.name || visitor.user.username || '?')[0].toUpperCase()}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-medium text-sm truncate text-green-800">
                                                                {visitor.user.name || visitor.user.username}
                                                            </span>
                                                            <Badge variant="outline" className="text-[10px] border-green-300 text-green-600">
                                                                Autentificat
                                                            </Badge>
                                                        </div>
                                                        <p className="text-[10px] text-muted-foreground truncate">
                                                            {visitor.user.email}
                                                        </p>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                                                        <Eye className="w-4 h-4 text-gray-500" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <span className="font-medium text-sm text-gray-600">Vizitator anonim</span>
                                                        <p className="text-[10px] text-muted-foreground">
                                                            {ua.browser} • {ua.device}
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="flex flex-col sm:items-end gap-1 text-[10px] sm:text-xs shrink-0">
                                            <a
                                                href={visitor.current_page?.startsWith('/') ? visitor.current_page : `/${visitor.current_page}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-mono text-[10px] px-2 py-0.5 rounded bg-secondary hover:bg-primary hover:text-white transition-colors cursor-pointer"
                                                title={`Deschide: ${visitor.current_page}`}
                                            >
                                                {visitor.current_page}
                                            </a>
                                            <button
                                                onClick={() => {
                                                    setIpFilter(visitor.ip_address);
                                                    setIpSearch(visitor.ip_address);
                                                    setPage(0);
                                                    setTimeout(() => {
                                                        document.getElementById('visitors-table')?.scrollIntoView({ behavior: 'smooth' });
                                                    }, 100);
                                                }}
                                                className="font-mono text-primary hover:underline cursor-pointer"
                                                title={visitor.ip_address}
                                            >
                                                {visitor.hostname || visitor.ip_address}
                                            </button>
                                            <span className="text-muted-foreground truncate max-w-[150px] sm:max-w-[200px]" title={visitor.user_agent}>
                                                {visitor.user_agent?.substring(0, 40)}...
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Chart */}
            <Card className="overflow-hidden">
                <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 sm:p-6">
                    <div>
                        <CardTitle className="text-base sm:text-xl">Grafic vizitatori</CardTitle>
                        <CardDescription className="text-[10px] sm:text-sm">
                            {period === '1' ? 'Vizite pe ore' : `Ultimele ${period} zile`}
                        </CardDescription>
                    </div>
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-24 sm:w-32 h-8">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">1 zi</SelectItem>
                            <SelectItem value="7">7 zile</SelectItem>
                            <SelectItem value="14">14 zile</SelectItem>
                            <SelectItem value="30">30 zile</SelectItem>
                        </SelectContent>
                    </Select>
                </CardHeader>
                <CardContent className="p-1 sm:p-6 pt-0">
                    {loadingStats ? (
                        <Skeleton className="h-[160px] sm:h-[200px] w-full" />
                    ) : (period === '1' && stats?.hourly?.length > 0) ? (
                        <div className="w-full h-[160px] sm:h-[200px]" style={{ minWidth: 0 }}>
                        <ResponsiveContainer width="99%" height="100%">
                            <AreaChart data={stats.hourly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorHourlyVisits" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey="hour" 
                                    tick={{ fontSize: 8 }}
                                    tickFormatter={(val) => `${val}h`}
                                    interval="preserveStartEnd"
                                />
                                <YAxis tick={{ fontSize: 8 }} width={25} />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: 'white', 
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '10px'
                                    }}
                                    labelFormatter={(val) => `Ora: ${val}:00`}
                                    formatter={(value) => [value, 'Vizite']}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="count" 
                                    name="Vizite"
                                    stroke="#3b82f6" 
                                    fillOpacity={1}
                                    fill="url(#colorHourlyVisits)" 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                        </div>
                    ) : stats?.daily?.length > 0 ? (
                        <div className="w-full h-[160px] sm:h-[200px]" style={{ minWidth: 0 }}>
                        <ResponsiveContainer width="99%" height="100%">
                            <AreaChart data={stats.daily} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorUnique" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey="date" 
                                    tick={{ fontSize: 8 }}
                                    tickFormatter={(val) => {
                                        const parts = val.split('-');
                                        return `${parts[2]}/${parts[1]}`;
                                    }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis tick={{ fontSize: 8 }} width={25} />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: 'white', 
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '10px'
                                    }}
                                    labelFormatter={(val) => `Data: ${val}`}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="visits" 
                                    name="Vizite"
                                    stroke="#3b82f6" 
                                    fillOpacity={1}
                                    fill="url(#colorVisits)" 
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="unique_visitors" 
                                    name="Unici"
                                    stroke="#22c55e" 
                                    fillOpacity={1}
                                    fill="url(#colorUnique)" 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-[160px] sm:h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                            Nu există date
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Unique Visitors Chart - Separate */}
            <Card className="overflow-hidden">
                <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 sm:p-6">
                    <div>
                        <CardTitle className="text-base sm:text-xl">Vizitatori unici</CardTitle>
                        <CardDescription className="text-[10px] sm:text-sm">
                            {uniquePeriod === '1' ? 'Pe ore' : `Ultimele ${uniquePeriod} zile`}
                        </CardDescription>
                    </div>
                    <Select value={uniquePeriod} onValueChange={setUniquePeriod}>
                        <SelectTrigger className="w-24 sm:w-32 h-8">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">1 zi</SelectItem>
                            <SelectItem value="7">7 zile</SelectItem>
                            <SelectItem value="14">14 zile</SelectItem>
                            <SelectItem value="30">30 zile</SelectItem>
                        </SelectContent>
                    </Select>
                </CardHeader>
                <CardContent className="p-1 sm:p-6 pt-0">
                    {loadingUniqueStats ? (
                        <Skeleton className="h-[140px] sm:h-[180px] w-full" />
                    ) : (uniquePeriod === '1' && uniqueStats?.hourly?.length > 0) ? (
                        <div className="w-full h-[140px] sm:h-[180px]" style={{ minWidth: 0 }}>
                        <ResponsiveContainer width="99%" height="100%">
                            <AreaChart data={uniqueStats.hourly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorHourlyUnique" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey="hour" 
                                    tick={{ fontSize: 8 }}
                                    tickFormatter={(val) => `${val}h`}
                                    interval="preserveStartEnd"
                                />
                                <YAxis tick={{ fontSize: 8 }} width={25} />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: 'white', 
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '10px'
                                    }}
                                    labelFormatter={(val) => `Ora: ${val}:00`}
                                    formatter={(value) => [value, 'Unici']}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="unique_count" 
                                    name="Unici"
                                    stroke="#22c55e" 
                                    fillOpacity={1}
                                    fill="url(#colorHourlyUnique)" 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                        </div>
                    ) : uniqueStats?.daily?.length > 0 ? (
                        <div className="w-full h-[140px] sm:h-[180px]" style={{ minWidth: 0 }}>
                        <ResponsiveContainer width="99%" height="100%">
                            <AreaChart data={uniqueStats.daily} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorDailyUnique" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey="date" 
                                    tick={{ fontSize: 8 }}
                                    tickFormatter={(val) => {
                                        const parts = val.split('-');
                                        return `${parts[2]}/${parts[1]}`;
                                    }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis tick={{ fontSize: 8 }} width={25} />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: 'white', 
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '10px'
                                    }}
                                    labelFormatter={(val) => `Data: ${val}`}
                                    formatter={(value) => [value, 'Unici']}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="unique_visitors" 
                                    name="Unici"
                                    stroke="#22c55e" 
                                    fillOpacity={1}
                                    fill="url(#colorDailyUnique)" 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-[140px] sm:h-[180px] flex items-center justify-center text-muted-foreground text-sm">
                            Nu există date
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Top Pages */}
            {stats?.top_pages?.length > 0 && (
                <Card className="overflow-hidden">
                    <CardHeader className="p-3 sm:p-6">
                        <CardTitle className="text-base sm:text-xl">Pagini populare</CardTitle>
                        <CardDescription className="text-[10px] sm:text-sm">Top 10 - Click pentru a vedea IP-urile</CardDescription>
                    </CardHeader>
                    <CardContent className="p-2 sm:p-6 pt-0">
                        <div className="space-y-1">
                            {stats.top_pages.map((page, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        setUrlFilter(page.path);
                                        setPage(0);
                                        // Scroll to visitors table
                                        setTimeout(() => {
                                            document.getElementById('visitors-table')?.scrollIntoView({ behavior: 'smooth' });
                                        }, 100);
                                    }}
                                    className={`w-full flex flex-col sm:flex-row sm:items-center justify-between p-2 rounded-lg hover:bg-primary/10 gap-1 sm:gap-2 text-left transition-colors ${urlFilter === page.path ? 'bg-primary/10 ring-1 ring-primary' : 'hover:bg-muted/50'}`}
                                >
                                    <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                                        <span className="text-[10px] sm:text-sm font-medium text-muted-foreground w-4 shrink-0">{idx + 1}.</span>
                                        <span className="text-[10px] sm:text-sm font-mono truncate">{page.path}</span>
                                    </div>
                                    <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                                        <Badge variant="outline" className="text-[10px] sm:text-xs">
                                            {page.unique_visitors || 0} unici
                                        </Badge>
                                        <Badge variant="secondary" className="text-[10px] sm:text-xs">
                                            {page.count} vizite
                                        </Badge>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Visitors Table */}
            <Card className="overflow-hidden" id="visitors-table">
                <CardHeader className="flex flex-col gap-3 p-3 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                            <CardTitle className="text-base sm:text-xl">Vizitatori recenți</CardTitle>
                            <CardDescription className="text-[10px] sm:text-sm">
                                {urlFilter 
                                    ? `${total} vizite pentru: ${urlFilter}` 
                                    : ipFilter 
                                        ? `${total} vizite IP: ${ipFilter}` 
                                        : `${total} vizite`
                                }
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            {urlFilter && (
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => {
                                        setUrlFilter('');
                                        setPage(0);
                                    }}
                                    className="h-8 text-xs"
                                >
                                    <X className="w-3 h-3 mr-1" />
                                    Șterge filtru URL
                                </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={fetchVisitors} className="self-start sm:self-auto h-8">
                                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                <span className="text-xs">Refresh</span>
                            </Button>
                        </div>
                    </div>
                    
                    {/* Active URL Filter Badge */}
                    {urlFilter && (
                        <div className="flex items-center gap-2 p-2 bg-primary/5 rounded-lg">
                            <span className="text-xs text-muted-foreground">Filtrat după URL:</span>
                            <Badge variant="outline" className="font-mono text-xs">{urlFilter}</Badge>
                            {uniqueIps.length > 0 && (
                                <span className="text-xs text-muted-foreground ml-auto">
                                    {uniqueIps.length} IP-uri unice
                                </span>
                            )}
                        </div>
                    )}
                    
                    {/* IP Search and Filter */}
                    <div className="flex flex-col gap-2">
                        {/* Search Input */}
                        <div className="flex gap-2">
                            <div className="relative flex-1 min-w-0">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                                <Input
                                    placeholder="Caută IP..."
                                    value={ipSearch}
                                    onChange={(e) => setIpSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            setIpFilter(ipSearch);
                                            setPage(0);
                                        }
                                    }}
                                    className="pl-7 pr-7 h-8 text-xs"
                                />
                                {ipSearch && (
                                    <button
                                        onClick={() => {
                                            setIpSearch('');
                                            setIpFilter('');
                                            setPage(0);
                                        }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                            
                            {/* Search Button */}
                            <Button 
                                onClick={() => {
                                    setIpFilter(ipSearch);
                                    setPage(0);
                                }}
                                disabled={!ipSearch}
                                className="shrink-0 h-8 px-2"
                                size="sm"
                            >
                                <Search className="w-3 h-3" />
                            </Button>
                        </div>
                        
                        <div className="flex gap-2">
                            {/* IP Dropdown Filter */}
                            {uniqueIps.length > 0 && (
                                <Select 
                                    value={ipFilter} 
                                    onValueChange={(value) => {
                                        setIpFilter(value === 'all' ? '' : value);
                                        setIpSearch(value === 'all' ? '' : value);
                                        setPage(0);
                                    }}
                                >
                                    <SelectTrigger className="flex-1 min-w-0 h-8 text-xs">
                                        <SelectValue placeholder="Filtrează IP" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toate IP-urile</SelectItem>
                                        {uniqueIps.map((ip) => (
                                            <SelectItem key={ip} value={ip}>
                                                {ip}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            
                            {/* Clear Filter Button */}
                            {ipFilter && (
                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        setIpFilter('');
                                        setIpSearch('');
                                        setPage(0);
                                    }}
                                    className="shrink-0 h-8 px-2"
                                    size="sm"
                                >
                                    <X className="w-3 h-3" />
                                </Button>
                            )}
                        </div>
                        
                        {/* Active URL Filter Indicator */}
                        {urlFilter && (
                            <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-muted-foreground">Filtru URL:</span>
                                <Badge variant="secondary" className="text-xs font-mono max-w-[200px] truncate">
                                    {urlFilter}
                                </Badge>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => {
                                        setUrlFilter('');
                                        setPage(0);
                                    }}
                                    className="h-6 px-1"
                                >
                                    <X className="w-3 h-3" />
                                </Button>
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="p-2 sm:p-6 pt-0">
                    {loading ? (
                        <div className="space-y-2">
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} className="h-10 w-full" />
                            ))}
                        </div>
                    ) : visitors.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p className="text-xs">
                                {ipFilter && urlFilter 
                                    ? `Nu există vizite de la IP: ${ipFilter} pentru URL: ${urlFilter}`
                                    : ipFilter 
                                        ? `Nu există vizite de la IP: ${ipFilter}` 
                                        : urlFilter 
                                            ? `Nu există vizite pentru URL: ${urlFilter}`
                                            : 'Nu există vizite'}
                            </p>
                            {(ipFilter || urlFilter) && (
                                <Button 
                                    variant="outline" 
                                    className="mt-4"
                                    onClick={() => {
                                        setIpFilter('');
                                        setIpSearch('');
                                        setUrlFilter('');
                                        setPage(0);
                                    }}
                                >
                                    Șterge filtrele
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Mobile Card Layout */}
                            <div className="sm:hidden space-y-3">
                                {visitors.map((visitor) => {
                                    const ua = parseUserAgent(visitor.user_agent);
                                    return (
                                        <div key={visitor.visit_id} className="p-3 border rounded-lg bg-card">
                                            <div className="flex items-center justify-between mb-2">
                                                <button
                                                    onClick={() => {
                                                        setIpFilter(visitor.ip);
                                                        setIpSearch(visitor.ip);
                                                        setPage(0);
                                                    }}
                                                    className={`px-2 py-1 rounded text-xs font-mono transition-colors ${
                                                        ipFilter === visitor.ip 
                                                            ? 'bg-primary text-primary-foreground' 
                                                            : 'bg-muted hover:bg-primary/20'
                                                    }`}
                                                >
                                                    {visitor.ip}
                                                </button>
                                                <span className="text-xs text-muted-foreground">
                                                    {formatDate(visitor.timestamp)}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setUrlFilter(visitor.path);
                                                    setPage(0);
                                                }}
                                                className={`text-xs font-mono truncate mb-2 block w-full text-left transition-colors ${
                                                    urlFilter === visitor.path 
                                                        ? 'text-primary font-semibold' 
                                                        : 'text-muted-foreground hover:text-primary'
                                                }`}
                                            >
                                                {visitor.path}
                                            </button>
                                            <div className="flex items-center gap-2 text-xs">
                                                {ua.device === 'Mobile' ? (
                                                    <Smartphone className="w-3 h-3 text-blue-500" />
                                                ) : (
                                                    <Monitor className="w-3 h-3 text-gray-500" />
                                                )}
                                                <span>{ua.device}</span>
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                    {ua.browser}
                                                </Badge>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Desktop Table Layout */}
                            <div className="hidden sm:block overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Data & Ora</TableHead>
                                            <TableHead>IP</TableHead>
                                            <TableHead>Pagină</TableHead>
                                            <TableHead>Dispozitiv</TableHead>
                                            <TableHead>Browser</TableHead>
                                            <TableHead>User Agent</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {visitors.map((visitor) => {
                                            const ua = parseUserAgent(visitor.user_agent);
                                            return (
                                                <TableRow key={visitor.visit_id}>
                                                    <TableCell className="whitespace-nowrap">
                                                        {formatDate(visitor.timestamp)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <button
                                                            onClick={() => {
                                                                setIpFilter(visitor.ip);
                                                                setIpSearch(visitor.ip);
                                                                setPage(0);
                                                            }}
                                                            className={`px-2 py-1 rounded text-xs font-mono transition-colors ${
                                                                ipFilter === visitor.ip 
                                                                    ? 'bg-primary text-primary-foreground' 
                                                                    : 'bg-muted hover:bg-primary/20'
                                                            }`}
                                                            title="Click pentru a filtra după acest IP"
                                                        >
                                                            {visitor.ip}
                                                        </button>
                                                    </TableCell>
                                                    <TableCell className="max-w-[200px] truncate">
                                                        <button
                                                            onClick={() => {
                                                                setUrlFilter(visitor.path);
                                                                setPage(0);
                                                            }}
                                                            className={`font-mono text-xs transition-colors ${
                                                                urlFilter === visitor.path 
                                                                    ? 'text-primary font-semibold' 
                                                                    : 'text-muted-foreground hover:text-primary'
                                                            }`}
                                                            title="Click pentru a filtra după acest URL"
                                                        >
                                                            {visitor.path}
                                                        </button>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-1">
                                                            {ua.device === 'Mobile' ? (
                                                                <Smartphone className="w-4 h-4 text-blue-500" />
                                                            ) : (
                                                                <Monitor className="w-4 h-4 text-gray-500" />
                                                            )}
                                                            <span className="text-xs">{ua.device}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="text-xs">
                                                            {ua.browser}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="max-w-[300px]">
                                                        <span className="text-xs text-muted-foreground truncate block">
                                                            {visitor.user_agent?.substring(0, 80)}...
                                                        </span>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                                    <p className="text-xs sm:text-sm text-muted-foreground">
                                        Pagina {page + 1} din {totalPages}
                                    </p>
                                    <div className="flex gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => setPage(p => Math.max(0, p - 1))}
                                            disabled={page === 0}
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                            disabled={page >= totalPages - 1}
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Unique Visitors Today Modal */}
            <Dialog open={uniqueTodayModal} onOpenChange={setUniqueTodayModal}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-4 sm:p-6 z-[200]">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                            <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                            Vizitatori unici azi
                            <Badge variant="secondary" className="ml-1 text-[10px] sm:text-xs">
                                {uniqueTodayVisitors.length}
                            </Badge>
                        </DialogTitle>
                        {/* Summary badges */}
                        <div className="flex gap-2 mt-2">
                            <Badge variant="outline" className="text-green-600 border-green-300 text-[10px] sm:text-xs">
                                <User className="w-3 h-3 mr-1" />
                                {uniqueTodayVisitors.filter(v => v.is_logged_in).length} auth
                            </Badge>
                            <Badge variant="outline" className="text-gray-600 text-[10px] sm:text-xs">
                                <Eye className="w-3 h-3 mr-1" />
                                {uniqueTodayVisitors.filter(v => !v.is_logged_in).length} anon
                            </Badge>
                        </div>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto -mx-4 px-4 sm:mx-0 sm:px-0" onClick={() => { setSelectedVisitorPages(null); setSelectedVisitorActions(null); setExpandedPagesIp(null); }}>
                        {loadingUniqueTodayVisitors ? (
                            <div className="space-y-2 p-4">
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className="h-16 w-full" />
                                ))}
                            </div>
                        ) : uniqueTodayVisitors.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Niciun vizitator unic azi</p>
                            </div>
                        ) : (
                            <>
                                {/* Mobile: Card layout */}
                                <div className="sm:hidden space-y-2">
                                    {uniqueTodayVisitors.map((visitor, idx) => {
                                        const ua = parseUserAgent(visitor.user_agent);
                                        const isAuth = visitor.is_logged_in;
                                        return (
                                            <div 
                                                key={idx} 
                                                className={`p-3 rounded-lg border ${isAuth ? 'bg-green-50/50 border-green-200' : 'bg-white border-gray-200'}`}
                                            >
                                                {/* Header row */}
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <div className="flex-1 min-w-0">
                                                        {isAuth ? (
                                                            <div className="flex flex-col gap-0.5">
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    <Badge variant="default" className="bg-green-600 text-[9px] px-1.5 py-0">
                                                                        Auth
                                                                    </Badge>
                                                                    <button
                                                                        onClick={() => {
                                                                            setUniqueTodayModal(false);
                                                                            window.location.href = `/user/${visitor.username || visitor.user_id}`;
                                                                        }}
                                                                        className="font-medium text-sm truncate text-primary hover:underline"
                                                                    >
                                                                        {visitor.name || 'Utilizator'}
                                                                    </button>
                                                                </div>
                                                                <button
                                                                    onClick={() => {
                                                                        setIpFilter(visitor.ip_address);
                                                                        setIpSearch(visitor.ip_address);
                                                                        setPage(0);
                                                                        setUniqueTodayModal(false);
                                                                    }}
                                                                    className="font-mono text-[10px] text-primary hover:underline text-left truncate block max-w-full"
                                                                >
                                                                    {visitor.hostname || visitor.ip_address}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => {
                                                                    setIpFilter(visitor.ip_address);
                                                                    setIpSearch(visitor.ip_address);
                                                                    setPage(0);
                                                                    setUniqueTodayModal(false);
                                                                }}
                                                                className="font-mono text-xs text-primary hover:underline text-left truncate block max-w-full"
                                                            >
                                                                {visitor.hostname || visitor.ip_address}
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        {ua.device === 'Mobile' ? (
                                                            <Smartphone className="w-3 h-3 text-blue-500" />
                                                        ) : (
                                                            <Monitor className="w-3 h-3 text-gray-500" />
                                                        )}
                                                        <span className="text-[10px] text-muted-foreground">{ua.browser}</span>
                                                    </div>
                                                </div>
                                                {/* Stats row */}
                                                <div className="flex items-center justify-between text-[10px]">
                                                    <div className="flex items-center gap-3">
                                                        {/* Actions - clickable for authenticated users */}
                                                        {isAuth && visitor.actions && visitor.actions.length > 0 ? (
                                                            <span 
                                                                className="text-primary cursor-pointer hover:underline"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const isCurrentlyOpen = selectedVisitorActions?.visitorKey === (visitor.user_id || visitor.ip_address);
                                                                    setSelectedVisitorActions(
                                                                        isCurrentlyOpen 
                                                                            ? null 
                                                                            : { visitorKey: visitor.user_id || visitor.ip_address, actions: visitor.actions || [] }
                                                                    );
                                                                    // Close pages popup when opening actions
                                                                    if (!isCurrentlyOpen) {
                                                                        setSelectedVisitorPages(null);
                                                                    }
                                                                }}
                                                            >
                                                                <span className="font-medium">{visitor.activity_count || visitor.visit_count}</span> acțiuni
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted-foreground">
                                                                <span className="font-medium text-foreground">{visitor.visit_count}</span> vizite
                                                            </span>
                                                        )}
                                                        {/* Pages link */}
                                                        <span 
                                                            className="text-primary cursor-pointer hover:underline"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const isCurrentlyOpen = selectedVisitorPages?.ip === visitor.ip_address;
                                                                setSelectedVisitorPages(
                                                                    isCurrentlyOpen 
                                                                        ? null 
                                                                        : { ip: visitor.ip_address, pages: visitor.pages || [] }
                                                                );
                                                                // Close actions popup and reset expanded state when toggling
                                                                if (!isCurrentlyOpen) {
                                                                    setExpandedPagesIp(null);
                                                                    setSelectedVisitorActions(null);
                                                                }
                                                            }}
                                                        >
                                                            {visitor.pages_visited} pagini
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="flex items-center gap-1 text-orange-600 font-medium">
                                                            <Clock className="w-3 h-3" />
                                                            {formatTimeSpent(visitor.first_visit, visitor.last_visit)}
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* Actions popup for mobile (authenticated users) */}
                                                {selectedVisitorActions?.visitorKey === (visitor.user_id || visitor.ip_address) && (
                                                    <div 
                                                        className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-xs"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="font-semibold mb-1 text-blue-700">Acțiuni ({visitor.activity_count || 0} total):</div>
                                                        <ul className="space-y-0.5">
                                                            {(visitor.actions_with_count || []).map((actionObj, i) => (
                                                                <li key={i} className="text-[10px] text-blue-600 flex justify-between">
                                                                    <span>{actionObj.action}</span>
                                                                    <span className="font-medium text-blue-800">×{actionObj.count}</span>
                                                                </li>
                                                            ))}
                                                            {/* Fallback to old format if actions_with_count not available */}
                                                            {!visitor.actions_with_count && (visitor.actions || []).map((action, i) => (
                                                                <li key={i} className="text-[10px] text-blue-600">{action}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {/* Pages popup for mobile */}
                                                {selectedVisitorPages?.ip === visitor.ip_address && (
                                                    <div 
                                                        className="mt-2 p-2 bg-gray-50 rounded border text-xs"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="font-semibold mb-1 text-muted-foreground">Pagini:</div>
                                                        {(visitor.pages || []).length === 0 ? (
                                                            <div className="text-muted-foreground">Nicio pagină</div>
                                                        ) : (
                                                            <ul className="space-y-0.5">
                                                                {(expandedPagesIp === visitor.ip_address 
                                                                    ? (visitor.pages || []) 
                                                                    : (visitor.pages || []).slice(0, 5)
                                                                ).map((page, i) => (
                                                                    <li key={i} className="font-mono truncate text-[10px]">{page}</li>
                                                                ))}
                                                                {(visitor.pages || []).length > 5 && expandedPagesIp !== visitor.ip_address && (
                                                                    <li 
                                                                        className="text-primary cursor-pointer hover:underline font-medium"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setExpandedPagesIp(visitor.ip_address);
                                                                        }}
                                                                    >
                                                                        +{visitor.pages.length - 5} more
                                                                    </li>
                                                                )}
                                                                {expandedPagesIp === visitor.ip_address && (visitor.pages || []).length > 5 && (
                                                                    <li 
                                                                        className="text-primary cursor-pointer hover:underline font-medium"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setExpandedPagesIp(null);
                                                                        }}
                                                                    >
                                                                        Ascunde
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Desktop: Table layout */}
                                <div className="hidden sm:block">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Vizitator</TableHead>
                                                <TableHead>Device / Browser</TableHead>
                                                <TableHead className="text-center">Activitate</TableHead>
                                                <TableHead className="text-center">Pagini</TableHead>
                                                <TableHead className="text-center">Timp pe site</TableHead>
                                                <TableHead>Prima vizită</TableHead>
                                                <TableHead>Ultima vizită</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {uniqueTodayVisitors.map((visitor, idx) => {
                                                const ua = parseUserAgent(visitor.user_agent);
                                                const isAuth = visitor.is_logged_in;
                                                return (
                                                    <TableRow key={idx} className={isAuth ? 'bg-green-50/50' : ''}>
                                                        <TableCell>
                                                            <div className="flex flex-col gap-1">
                                                                {isAuth ? (
                                                                    <>
                                                                        <div className="flex items-center gap-2">
                                                                            <Badge variant="default" className="bg-green-600 text-[10px]">
                                                                                Autentificat
                                                                            </Badge>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setUniqueTodayModal(false);
                                                                                    window.location.href = `/user/${visitor.username || visitor.user_id}`;
                                                                                }}
                                                                                className="font-medium text-sm text-primary hover:underline"
                                                                            >
                                                                                {visitor.name || 'Utilizator'}
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => {
                                                                            setIpFilter(visitor.ip_address);
                                                                            setIpSearch(visitor.ip_address);
                                                                            setPage(0);
                                                                            setUniqueTodayModal(false);
                                                                            setTimeout(() => {
                                                                                document.getElementById('visitors-table')?.scrollIntoView({ behavior: 'smooth' });
                                                                            }, 100);
                                                                        }}
                                                                        className="font-mono text-sm text-primary hover:underline cursor-pointer text-left"
                                                                        title={visitor.ip_address}
                                                                    >
                                                                        {visitor.hostname || visitor.ip_address}
                                                                    </button>
                                                                )}
                                                                {isAuth && visitor.ip_address && (
                                                                    <span className="text-[10px] text-muted-foreground font-mono">
                                                                        IP: {visitor.hostname || visitor.ip_address}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                {ua.device === 'Mobile' ? (
                                                                    <Smartphone className="w-4 h-4 text-blue-500" />
                                                                ) : (
                                                                    <Monitor className="w-4 h-4 text-gray-500" />
                                                                )}
                                                                <span className="text-xs text-muted-foreground">
                                                                    {ua.browser} / {ua.os}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center relative">
                                                            <div className="flex flex-col items-center gap-1">
                                                                <Badge 
                                                                    variant="secondary"
                                                                    className={isAuth && visitor.actions?.length > 0 ? "cursor-pointer hover:bg-primary hover:text-white transition-colors" : ""}
                                                                    onClick={(e) => {
                                                                        if (isAuth && visitor.actions?.length > 0) {
                                                                            e.stopPropagation();
                                                                            const key = visitor.user_id || visitor.ip_address;
                                                                            setSelectedVisitorActions(
                                                                                selectedVisitorActions?.visitorKey === key
                                                                                    ? null
                                                                                    : { visitorKey: key, actions: visitor.actions_with_count || visitor.actions }
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    {isAuth ? (visitor.activity_count || visitor.visit_count) : visitor.visit_count}
                                                                </Badge>
                                                                {isAuth && visitor.actions && visitor.actions.length > 0 && (
                                                                    <span className="text-[10px] text-muted-foreground">
                                                                        {visitor.actions.slice(0, 2).join(', ')}
                                                                        {visitor.actions.length > 2 && '...'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* Actions popup */}
                                                            {selectedVisitorActions?.visitorKey === (visitor.user_id || visitor.ip_address) && (
                                                                <div 
                                                                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 bg-white border rounded-lg shadow-lg p-3 min-w-[200px] max-w-[300px] max-h-[200px] overflow-auto"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className="text-xs font-semibold mb-2 text-muted-foreground">Toate acțiunile:</div>
                                                                    <ul className="space-y-1">
                                                                        {(visitor.actions_with_count || []).map((actionObj, i) => (
                                                                            <li key={i} className="text-xs flex justify-between">
                                                                                <span>{typeof actionObj === 'object' ? actionObj.action : actionObj}</span>
                                                                                {typeof actionObj === 'object' && (
                                                                                    <span className="font-medium text-primary">×{actionObj.count}</span>
                                                                                )}
                                                                            </li>
                                                                        ))}
                                                                        {(!visitor.actions_with_count || visitor.actions_with_count.length === 0) && visitor.actions?.map((action, i) => (
                                                                            <li key={i} className="text-xs">{action}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-center relative">
                                                            <Badge 
                                                                variant="outline" 
                                                                className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedVisitorPages(
                                                                        selectedVisitorPages?.ip === visitor.ip_address 
                                                                            ? null 
                                                                            : { ip: visitor.ip_address, pages: visitor.pages || [] }
                                                                    );
                                                                }}
                                                            >
                                                                {visitor.pages_visited}
                                                            </Badge>
                                                            {/* Pages popup */}
                                                            {selectedVisitorPages?.ip === visitor.ip_address && (
                                                                <div 
                                                                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 bg-white border rounded-lg shadow-lg p-2 min-w-[200px] max-w-[300px] max-h-[200px] overflow-auto"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className="text-xs font-semibold mb-2 text-muted-foreground">Pagini vizitate:</div>
                                                                    {(visitor.pages || []).length === 0 ? (
                                                                        <div className="text-xs text-muted-foreground">Nicio pagină</div>
                                                                    ) : (
                                                                        <ul className="space-y-1">
                                                                            {(visitor.pages || []).map((page, i) => (
                                                                                <li key={i} className="text-xs font-mono truncate hover:text-primary">
                                                                                    {page}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <span className="flex items-center justify-center gap-1 text-orange-600 font-medium text-xs">
                                                                <Clock className="w-3 h-3" />
                                                                {formatTimeSpent(visitor.first_visit, visitor.last_visit)}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-xs text-muted-foreground">
                                                            {new Date(visitor.first_visit).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                                                        </TableCell>
                                                        <TableCell className="text-xs text-muted-foreground">
                                                            {new Date(visitor.last_visit).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminVisitorsTab;
